import React, { useEffect, useCallback, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

// Assume these icons are imported from an icon library
import { useSidebar } from "../context/SidebarContext";
import { ChevronDownIcon, HorizontaLDots } from "../icons";
import { Menus, Other } from "../menu/menu";
import { getItem } from "../utils/storage";
import { useMenus } from "../hooks/useRoleMenu";
import DynamicIcon from "../components/dynamicIcon/dynamicIcon";

type NavItem = {
  name: string;
  icon: any;
  path?: string;
  role?: any;
  subItems?: {
    role?: any;
    name: string;
    path: string;
    pro?: boolean;
    new?: boolean;
  }[];
};

const user = getItem("user");

const userRole = user?.user.role;

export const filterMneus = Menus.filter((item, _) =>
  item.role.includes(userRole),
);

const AppSidebar: React.FC = () => {
  const menus = useMenus(userRole);
  const navItems: NavItem[] = menus;
  const othersItems: NavItem[] = Other;

  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();

  const [openSubmenu, setOpenSubmenu] = useState<{
    // type: "main" | "others";
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {},
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // const isActive = useCallback(
  //   (path: string) => location.pathname === path,
  //   [location.pathname],
  // );

  const isActive = useCallback(
    (path: string) => location.pathname.startsWith(path),
    [location.pathname],
  );

  useEffect(() => {
    if (!navItems.length) return; // Ensure menus are available

    let submenuMatched = false;
    ["main", "others"].forEach((menuType) => {
      const items = menuType === "main" ? navItems : othersItems;
      items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({ index });
              submenuMatched = true;
            }
          });
        }
      });
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location.pathname, navItems]); // Ensure it runs when API data updates

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number) => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        // prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { index };
    });
  };

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-indigo-50 dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200 
        ${
          isExpanded || isMobileOpen
            ? "w-[290px]"
            : isHovered
              ? "w-[290px]"
              : "w-[90px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-4 flex ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link to="/">
          {isExpanded || isHovered || isMobileOpen ? (
            <>
              <img
                className="dark:hidden"
                src="/images/logo/tms-light.svg"
                alt="Logo"
                width={106}
                height={40}
              />
              <img
                className="hidden dark:block"
                src="/images/logo/tms-dark.svg"
                alt="Logo"
                width={106}
                height={40}
              />
            </>
          ) : (
            <img
              src="/images/logo/tms-icon.svg"
              alt="Logo"
              width={32}
              height={32}
            />
          )}
        </Link>
      </div>
      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-6">
          <div className="flex flex-col gap-4">
            <div>
              <h2
                className={`mb-4 text-xs uppercase flex leading-[20px] text-gray-400 ${
                  !isExpanded && !isHovered
                    ? "lg:justify-center"
                    : "justify-start"
                }`}
              >
                {isExpanded || isHovered || isMobileOpen ? (
                  "Menu"
                ) : (
                  <HorizontaLDots />
                )}
              </h2>
              <ul className="flex flex-col gap-4">
                {navItems.map((nav, index) => {
                  //const menuType =  "main" | "others"
                  const { icon } = nav;
                  return (
                    <li key={nav.name}>
                      {nav.subItems ? (
                        <button
                          onClick={() => handleSubmenuToggle(index)}
                          className={`menu-item group ${
                            openSubmenu?.index === index
                              ? "menu-item-active"
                              : "menu-item-inactive"
                          } cursor-pointer ${
                            !isExpanded && !isHovered
                              ? "lg:justify-center"
                              : "lg:justify-start"
                          }`}
                        >
                          <span
                            className={`${
                              openSubmenu?.index === index
                                ? "menu-item-icon-active"
                                : "menu-item-icon-inactive"
                            }`}
                          >
                            <DynamicIcon
                              iconName={icon as any}
                              size={20}
                              color="red"
                            />
                            {/* <Icon className="size-5" /> */}
                          </span>
                          {(isExpanded || isHovered || isMobileOpen) && (
                            <span className="menu-item-text">{nav.name}</span>
                          )}
                          {(isExpanded || isHovered || isMobileOpen) && (
                            <ChevronDownIcon
                              className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                                // openSubmenu?.type === menuType &&
                                openSubmenu?.index === index
                                  ? "rotate-180 text-brand-500"
                                  : ""
                              }`}
                            />
                          )}
                        </button>
                      ) : (
                        nav.path && (
                          <Link
                            to={nav.path}
                            className={`menu-item group ${
                              isActive(nav.path)
                                ? "menu-item-active"
                                : "menu-item-inactive"
                            }`}
                          >
                            <span
                              className={`${
                                isActive(nav.path)
                                  ? "menu-item-icon-active"
                                  : "menu-item-icon-inactive"
                              }`}
                            >
                              <DynamicIcon
                                iconName={icon as any}
                                size={20}
                                // color="indigo"
                              />
                            </span>
                            {(isExpanded || isHovered || isMobileOpen) && (
                              <span className="menu-item-text">{nav.name}</span>
                            )}
                          </Link>
                        )
                      )}
                      {nav.subItems &&
                        (isExpanded || isHovered || isMobileOpen) && (
                          <div
                            ref={(el) => {
                              subMenuRefs.current[`${index}`] = el;
                            }}
                            className="overflow-hidden transition-all duration-300"
                            style={{
                              height:
                                //openSubmenu?.type === menuType &&
                                openSubmenu?.index === index
                                  ? `${subMenuHeight[`${index}`]}px`
                                  : "0px",
                            }}
                          >
                            <ul className="mt-2 space-y-1 ml-9">
                              {nav.subItems.map((subItem) => (
                                <li key={subItem.name}>
                                  <Link
                                    to={subItem.path}
                                    className={`menu-dropdown-item ${
                                      isActive(subItem.path)
                                        ? "menu-dropdown-item-active"
                                        : "menu-dropdown-item-inactive"
                                    }`}
                                  >
                                    {subItem.name}
                                    <span className="flex items-center gap-1 ml-auto">
                                      {subItem.new && (
                                        <span
                                          className={`ml-auto ${
                                            isActive(subItem.path)
                                              ? "menu-dropdown-badge-active"
                                              : "menu-dropdown-badge-inactive"
                                          } menu-dropdown-badge`}
                                        >
                                          new
                                        </span>
                                      )}
                                      {subItem.pro && (
                                        <span
                                          className={`ml-auto ${
                                            isActive(subItem.path)
                                              ? "menu-dropdown-badge-active"
                                              : "menu-dropdown-badge-inactive"
                                          } menu-dropdown-badge`}
                                        >
                                          pro
                                        </span>
                                      )}
                                    </span>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                    </li>
                  );
                })}
              </ul>
              {/* {renderMenuItems(navItems, "main")} */}
            </div>

            {/* <div className="">
              <h2
                className={`mb-4 text-xs uppercase flex leading-[20px] text-gray-400 ${
                  !isExpanded && !isHovered
                    ? "lg:justify-center"
                    : "justify-start"
                }`}
              >
                {isExpanded || isHovered || isMobileOpen ? (
                  "Others"
                ) : (
                  <HorizontaLDots />
                )}
              </h2>
              {renderMenuItems(othersItems, "others")}
            </div> */}
          </div>
        </nav>
        {/* {isExpanded || isHovered || isMobileOpen ? <SidebarWidget /> : null} */}
      </div>
    </aside>
  );
};

export default AppSidebar;
